import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchCountry, type CountryRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A11.03 — Davlat kartasi. Alohida endpoint yo'q — `/admin/countries` ro'yxatidan
 * kod bo'yicha olinadi (kod/nom/dial/flag/millat/blok holati/ro'yxatdan o'tganlar).
 */
export function CountryCard() {
  const t = useT()
  const { can } = useAuth()
  const { code = '' } = useParams()
  const [country, setCountry] = useState<CountryRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchCountry(code, ac.signal)
      .then((c) => {
        if (alive) setCountry(c)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [code, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const actions = (
    <Link
      to="/geo"
      className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
    >
      <ChevronLeft size={13} />
      {t.nav.geo}
    </Link>
  )

  return (
    <>
      <PageHeader
        title={country ? `${country.flag ?? ''} ${country.name}`.trim() : t.geo.countryCard}
        screenId="A11.03"
        actions={actions}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !country ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3">
              <StatusBadge status={country.is_blocked ? 'banned' : 'active'} />
            </div>
            <Field label={t.col.code}>
              <MonoValue value={country.code} />
            </Field>
            <Field label={t.col.country}>{country.name}</Field>
            <Field label={t.col.nationality}>{country.nationality || '—'}</Field>
            <Field label={t.col.dialCode}>
              <MonoValue value={country.dial_code} />
            </Field>
            <Field label={t.col.registrations}>
              <span className="font-mono tabular">{country.users.toLocaleString()}</span>
            </Field>
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
